import { useEffect } from 'react'
import type { FC, PropsWithChildren } from 'react'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ErrorBoundary } from 'react-error-boundary'
import { ToastContainer } from 'react-toastify'

import { getMs, CACHE_BUSTER, isIOS, isCordovaApp } from '@/helpers'
import { initCordovaCookieManager } from '@/helpers/cordova-cookie-manager'
import { requestLocationAccuracy } from '@/helpers/request-location-accuracy'
import { createIDBPersister } from '@/helpers/query-persister'
import { isDebugAuthEnabled, isVisitorUnauthorizedError } from '@/helpers/request'
import { rqKeys } from '@/react-query-keys'
import { useAppStore } from '@/store'
import { useNetworkRefetch } from '@/hooks/use-network-refetch'
import { useBackendNotifications } from '@/hooks/use-backend-notifications'
import { PullToRefresh } from '@/components'
import { TopBar, BottomBar } from '@/b-components'
import { MessagesOverlay, useMessagesUnreadBootstrap } from '@/pages/messages'
import { PWAInstallPrompt, PWAUpdatePrompt } from '@/b-components/pwa'
import { CordovaUpdatePrompt } from '@/b-components/cordova/cordova-update-prompt'
import { isStandalone } from '@/b-components/pwa/helpers'
// import { TutorialProvider } from '@/b-components/tutorial'
import { useBackButton } from '@/helpers/hooks/use-back-button'
import { useDynamicVH } from './hooks/use-dynamic-vh'
import { CheckAuth } from './check-auth'
import { AppGlobalStyled } from '@/app-global.styles'
import {
  SC_AppContainer,
  SC_AppWrapper,
} from './styled'


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: getMs('60d'), // keep cache longer (aligned with persist maxAge)
      staleTime: 0, // Данные всегда считаются устаревшими — при онлайне всегда идём в сеть
      refetchOnWindowFocus: true,
      refetchOnReconnect: true, // При восстановлении сети делаем refetch
      refetchOnMount: true,
      networkMode: 'always', // Запросы выполняются всегда (и онлайн, и офлайн)
      retry: (failureCount, error) => {
        // If we can't reauthorize (visitor-unauthorized), keep cached data and do NOT retry endlessly.
        if (isVisitorUnauthorizedError(error)) return false
        if ((error as any)?.code === 'visitor-unauthorized') return false
        if ((error as any)?.response?.code === 'visitor-unauthorized') return false
        // Do not retry while fully offline / network is unreachable.
        if ((error as any)?.code === 'ERR_NETWORK') return false
        if ((error as any)?.message === 'Network Error') return false
        if (!(error as any)?.response) return false
        return failureCount < 2
      },
      retryDelay: 1000,
    },
    mutations: {
      networkMode: 'always',
    },
  },
})

const persister = createIDBPersister()


// Внутренний компонент для использования хуков, требующих QueryClient
const AppLayoutInner: FC<PropsWithChildren> = ({ children }) => {
  useDynamicVH()
  useBackButton()
  useNetworkRefetch() // Автоматическая инвалидация при восстановлении сети
  useBackendNotifications() // Long-polling уведомлений бэкенда (точечные инвалидации)
  useMessagesUnreadBootstrap() // Общая инициализация unread для popup + dashboard

  const isPullToRefreshDisabled = useAppStore((s) => s.isPullToRefreshDisabled)

  useEffect(() => {
    // Runtime debugging (prod/stage safe):
    // - add `?debugAuth=1` to URL OR
    // - localStorage.setItem('ERA_DEBUG_AUTH', '1')
    if (!isDebugAuthEnabled()) return

    const unsub = queryClient.getQueryCache().subscribe((event: any) => {
      const query = event?.query
      const qk = query?.queryKey
      if (!Array.isArray(qk) || qk[0] !== rqKeys.userData) return

      const state = query?.state
      // eslint-disable-next-line no-console
      console.warn('[auth][debug] rqKeys.userData query event', {
        type: event?.type,
        fetchStatus: state?.fetchStatus,
        status: state?.status,
        dataUpdatedAt: state?.dataUpdatedAt,
        errorUpdatedAt: state?.errorUpdatedAt,
        failureCount: state?.fetchFailureCount,
        errorCode: (state?.error as any)?.code || (state?.error as any)?.response?.code,
      })
    })

    return () => {
      unsub()
    }
  }, [])

  useEffect(() => {
    if (isCordovaApp) {
      const initCordovaRuntime = async () => {
        initCordovaCookieManager()

        try {
          await requestLocationAccuracy()
          // Request permission if not granted
          navigator.geolocation.getCurrentPosition(() => {}, () => {})
        } catch (e) {
          console.error('Location init failed', e)
        }
      }

      initCordovaRuntime()
    }
  }, [])

  return (
    <CheckAuth>
      <ErrorBoundary
        FallbackComponent={() => <div>Error</div>}
      >
        <AppGlobalStyled />

        <SC_AppContainer>
          <TopBar />

          <SC_AppWrapper>
            <CordovaUpdatePrompt />

            {isCordovaApp || (isStandalone() && isIOS) ? (
              <PullToRefresh
                onRefresh={async () => {
                  await queryClient.invalidateQueries()
                  await queryClient.refetchQueries({ type: 'active' })
                }}
                disabled={isPullToRefreshDisabled}
              >
                {children}
              </PullToRefresh>
            ) : (
              children
            )}
          </SC_AppWrapper>

          <BottomBar />
        </SC_AppContainer>

        <MessagesOverlay />

        <PWAInstallPrompt />
        <PWAUpdatePrompt />

        <ToastContainer />

        {/* <TutorialProvider /> */}

        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      </ErrorBoundary>
    </CheckAuth>
  )
}


export const AppLayout: FC<PropsWithChildren> = ({
  children,
}) => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        // Держим кэш дольше, чтобы после долгого простоя/рефреша не пропадали данные (баланс/покупки),
        // особенно если сеть/сессия временно недоступны.
        maxAge: getMs('60d'),
        buster: CACHE_BUSTER,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            // ВАЖНО:
            // Если последний refetch упал (например, сессия протухла), status станет 'error',
            // но query.state.data может содержать последние успешные данные.
            // Их нужно сохранять, иначе после обновления страницы пользователь увидит "пусто".
            return query.state.data !== undefined
          },
          shouldRedactErrors: () => true,
        },
      }}
    >
      <AppLayoutInner>{children}</AppLayoutInner>
    </PersistQueryClientProvider>
  )
}
